import { renderCell, renderHeader } from '../components/table';
import { useMemo, useContext } from 'react';
import { Flex, Box, Center, Spinner } from '@chakra-ui/react';
import { Wrapper } from '../components/Wrapper';
import { BreakdownBy } from '../components/breakdown-by';
import { useDispatch, useSelector } from 'react-redux';
import {
  selectInterviewTable,
  selectInterviewTableLoading,
  selectInterviewReportsGroup,
} from '@/store/atsReports/interviews/selectors';
import { applyGroup } from '@/store/atsReports/interviews';
import { secondsToHumanTime } from '@/pages/reports/non-ats-reports/ats/table';
import {
  EmptyDataWrapper,
  isReportTableEmpty,
} from '../components/empty-data-wrapper';
import { TitleWithTooltip } from '../components/title-with-tooltip';
import { ReportSelectionContext } from '@/contextProviders/ReportSelection';
import { useKFlags } from '@/hooks/use-k-flags';
import { useDrillDownHandler } from '../drilldown/use-drilldownHeader';
import { Table } from '@/components/ats';
import { KText } from '@/ui-library/components/text';
import { HeaderCell } from '@/components/ats/Table/tableCells/HeaderCell';
import { KTag } from '@/ui-library/components/atoms/KTag';
import { STORAGE_KEYS } from '@/utils/storageKeys';
import { useFilterStorage } from '@/hooks/use-filter-storage';
import { useParams } from 'react-router-dom';
import { EmptyReportChart } from '@/pages/reports/custom-reports/components/ReportCharts/EmptyReportChart';

interface Option {
  label: string;
  id: number | string;
}

interface InterviewTableProps {
  isReadOnly?: boolean;
  isPrintView?: boolean;
  groupBy1?: Option;
  groupBy2?: Option;
  customData?: any[];
  customBreakdownChange?: (_key: string, _option: Option) => void;
  renderTitle?: () => React.ReactNode;
  onDrillDownOpen?: (_data: any) => void;
  isStaticReport?: boolean;
  onSortChange?: (_sortState: string) => void;
  isEditMode?: boolean;
  tableColStyles?: any;
  onTableColDataChange?: (_tableColData: {
    order: string[];
    visibility: Record<string, boolean>;
  }) => void;
  initialTableColData?: {
    order: string[];
    visibility: Record<string, boolean>;
  };
  skipLocalStorage?: boolean;
  feReportsV3?: boolean;
  isFullScreen?: boolean;
  isFilterApplied?: boolean;
}

const GROUP_BY_OPTIONS = [
  { id: null, label: 'Time', key: 'dt' },
  { id: 'job_name', label: 'Job name', key: 'g1.name' },
  { id: 'primary_recruiter', label: 'Primary recruiter', key: 'g1.name' },
  { id: 'primary_hm', label: 'Primary hiring manager', key: 'g1.name' },
  { id: 'department', label: 'Department', key: 'g1.name' },
  {
    id: 'interview_location',
    label: 'Location (on interview)',
    key: 'g1.name',
  },
  { id: 'interviewer', label: 'Interviewer', key: 'g1.name' },
  { id: 'stage', label: 'Stage' },
];

const BREAKDOWN_OPTIONS = [
  { id: null, label: 'None' },
  { id: 'job_name', label: 'Job name' },
  { id: 'job_type', label: 'Job type' },
  { id: 'primary_recruiter', label: 'Primary recruiter' },
  { id: 'primary_hm', label: 'Primary hiring manager' },
  { id: 'appl_source', label: 'Source (on application)' },
  { id: 'department', label: 'Department' },
  { id: 'interview_location', label: 'Location (on interviews)' },
  { id: 'interviewer', label: 'Interviewer' },
  { id: 'stage', label: 'Stage' },
];

export const InterviewTable: React.FC<InterviewTableProps> = ({
  isReadOnly = false,
  isPrintView = false,
  groupBy1,
  groupBy2,
  customData,
  customBreakdownChange,
  renderTitle,
  onDrillDownOpen = () => {},
  isStaticReport = false,
  onSortChange = (_sortState) => {},
  isEditMode = false,
  tableColStyles = {},
  onTableColDataChange = undefined,
  initialTableColData = undefined,
  skipLocalStorage = false,
  feReportsV3 = false,
  isFullScreen = false,
  isFilterApplied = false,
}) => {
  const dispatch = useDispatch();
  const { feDrilldown } = useKFlags();
  const { consumer } = useContext(ReportSelectionContext);
  const { reportID } = useParams();
  const reduxIsLoading = useSelector(selectInterviewTableLoading);
  const reduxGroup = useSelector(selectInterviewReportsGroup);
  const reduxHiresTable = useSelector(selectInterviewTable);

  const isHiresTableLoading = feReportsV3 ? false : reduxIsLoading;

  const group = feReportsV3 ? null : reduxGroup;

  const hiresTable = feReportsV3 ? [] : reduxHiresTable;

  const getGroupValue = (value: any, fallbackValue: any) =>
    feReportsV3 ? value : value || fallbackValue;

  const getCurrentGroupConfig = () => ({
    groupBy1: getGroupValue(groupBy1?.id, group?.group_by_1?.id),
    groupBy2: getGroupValue(groupBy2?.id, group?.group_by_2?.id),
  });

  const getStageData = (row: any, isMainRow: boolean, groupConfig: any) => {
    if (isMainRow && groupConfig.groupBy1 === 'stage') {
      return row.original.g1;
    }
    if (!isMainRow && groupConfig.groupBy2 === 'stage') {
      return row.original.g2;
    }
    return null;
  };

  const isSortingByStage = (isMainRow: boolean, groupConfig: any) => {
    return isMainRow
      ? groupConfig.groupBy1 === 'stage'
      : groupConfig.groupBy2 === 'stage';
  };

  const drilldownGroup = {
    group_by_1: getGroupValue(groupBy1, group?.group_by_1),
    group_by_2: getGroupValue(groupBy2, group?.group_by_2),
  };

  const getStorageKey = () => {
    const baseKey = isStaticReport
      ? `${STORAGE_KEYS.STATIC_REPORTS_FILTERS}`
      : `${STORAGE_KEYS.CUSTOM_REPORTS_FILTERS}`;

    const key = reportID ? `${baseKey}_${reportID}` : `${baseKey}_interviews`;

    return key;
  };

  const { getStoredFilters } = useFilterStorage({
    storageKey: getStorageKey(),
  });

  const isJobFiltered = getStoredFilters()?.selectedFilters?.some(
    (filter: any) => filter.filter_by.value === 'job_name'
  );

  const dataToUse = customData || hiresTable || [];
  const isDataEmpty = isReportTableEmpty(dataToUse, 'applicants_interviewed');

  const { handleDrillDown } = useDrillDownHandler(
    feDrilldown,
    onDrillDownOpen,
    isJobFiltered
  );

  // eslint-disable-next-line react-hooks/preserve-manual-memoization
  const columns = useMemo(() => {
    const groupConfig = getCurrentGroupConfig();
    const header =
      getGroupValue(groupBy1, group?.group_by_1) || GROUP_BY_OPTIONS[0];

    const formatValue = (value: string, isGroupStage: boolean = false) => {
      const stageMap: Record<string, string> = {
        Offer: 'Offer stages',
        Lead: 'Lead stages',
        Applied: 'Application review stages',
        Interview: 'Interview stages',
        Hired: 'Hired stages',
      };
      const valueMap: Record<string, string> = {
        'No location': 'No location',
        zoom: 'Zoom',
        onsite: 'Onsite',
        google_meet: 'Google Meet',
        phone_interview: 'Phone Interview',
        teams: 'Teams',
      };

      if (isGroupStage) {
        return stageMap[value] || valueMap[value] || value;
      }

      return valueMap[value] || value;
    };

    const headerColumn = renderHeader({
      accessor: 'header',
      title: header?.label || 'Name',
      width: 250,
      expand: false,
      sort: true,
      renderFooter: () => <KText size="sm-semi-bold">Total</KText>,
      groupBy: groupConfig.groupBy1,
      breakDownBy: groupConfig.groupBy2,
      isReadOnly,
    });

    const customHeaderColumn = {
      ...headerColumn,
      id: 'header',
      title: header?.label || 'Name',
      columnConfig: false,
      Cell: (props: any) => {
        const groupConfig = getCurrentGroupConfig();
        const isMainRow = props.row.depth === 0;
        const stageData = getStageData(props.row, isMainRow, groupConfig);
        const isDeleted = stageData?.deleted === true;

        const shouldSkipFormatting =
          (groupConfig.groupBy1 === 'job_name' &&
            groupConfig.groupBy2 === 'stage') ||
          (isJobFiltered &&
            groupConfig.groupBy1 === 'stage' &&
            !groupConfig.groupBy2) ||
          (isJobFiltered &&
            groupConfig.groupBy1 === 'stage' &&
            groupConfig.groupBy2 === 'job_name');

        const isGroupStage =
          groupConfig.groupBy1 === 'stage' || groupConfig.groupBy2 === 'stage';

        const formattedValue = shouldSkipFormatting
          ? formatValue(props.cell.value)
          : formatValue(props.cell.value, isGroupStage);

        const modifiedProps = {
          ...props,
          cell: {
            ...props.cell,
            value: formattedValue,
          },
        };

        return (
          <Flex alignItems="center" gap="8px">
            {headerColumn.Cell(modifiedProps)}
            {isDeleted && (
              <KTag
                value="Deleted"
                bgColor="pastel-1-light"
                color="pastel-1-dark"
              />
            )}
          </Flex>
        );
      },
      sortType: (rowA: any, rowB: any, columnId: string, desc: boolean) => {
        const groupConfig = getCurrentGroupConfig();
        const isMainRow = rowA.depth === 0 && rowB.depth === 0;
        const sortingByStage = isSortingByStage(isMainRow, groupConfig);

        if (!sortingByStage) {
          const a = rowA?.values?.header;
          const b = rowB?.values?.header;
          const aIsNil = a === null || a === undefined;
          const bIsNil = b === null || b === undefined;

          if (aIsNil && bIsNil) return 0;
          if (aIsNil) return -1;
          if (bIsNil) return 1;

          if (typeof a === 'number' && typeof b === 'number') {
            return a - b;
          }

          return String(a).localeCompare(String(b), undefined, {
            numeric: true,
            sensitivity: 'base',
          });
        }

        const stageDataA = getStageData(rowA, isMainRow, groupConfig);
        const stageDataB = getStageData(rowB, isMainRow, groupConfig);

        const isDeletedA = stageDataA?.deleted === true;
        const isDeletedB = stageDataB?.deleted === true;

        if (isDeletedA !== isDeletedB) {
          return isDeletedA ? (desc ? -1 : 1) : desc ? 1 : -1;
        }

        if (isDeletedA && isDeletedB) {
          return 0;
        }

        return 0;
      },
    };

    function createDrillDownColumn(
      title: string,
      accessor: string,
      renderContent: (_row: any) => string,
      drillDownHeader: string,
      footerRenderer?: (_info: any) => string
    ) {
      return {
        ...renderCell({
          title,
          accessor,
          width: isReadOnly ? 160 : 250,
          sort: true,
          renderFooter: footerRenderer
            ? (info) =>
                createDrillDownContent(
                  info?.rows?.[0]?.original,
                  title,
                  drillDownHeader,
                  () => footerRenderer(info),
                  true
                )
            : undefined,
          render: (_, row) => {
            return createDrillDownContent(
              row,
              title,
              drillDownHeader,
              renderContent
            );
          },
        }),
        id: accessor,
        title: title,
        columnConfig: true,
      };
    }

    function createDrillDownContent(
      row: any,
      title: string,
      drillDownHeader: string,
      contentRenderer?: (_row: any) => string,
      isFooter?: boolean
    ) {
      return (
        <Flex
          _hover={{ textDecoration: feDrilldown ? 'underline' : 'none' }}
          cursor={feDrilldown ? 'pointer' : 'default'}
          onClick={() =>
            handleDrillDown(
              row,
              drillDownHeader,
              isFooter,
              drilldownGroup,
              null,
              contentRenderer(row)?.replace(/\(\d+(\.\d+)?%\)/g, '')
            )
          }
        >
          <KText
            size={isFooter ? 'sm-semi-bold' : 'sm'}
            fontWeight={
              row.canExpand && row.depth === 0 && !isReadOnly ? '650' : '550'
            }
          >
            {contentRenderer ? contentRenderer(row) : ''}
          </KText>
        </Flex>
      );
    }

    function createFeedbackColumn(
      title: string,
      accessor: string,
      drillDownHeader: string,
      footerRenderer?: (_info: any) => string
    ) {
      return {
        ...renderCell({
          title,
          accessor,
          width: 100,
          sort: true,
          renderFooter: footerRenderer
            ? (info) =>
                createDrillDownContent(
                  info?.rows?.[0]?.original,
                  title,
                  drillDownHeader,
                  () => footerRenderer(info),
                  true
                )
            : undefined,
          render: (_, row) => {
            return createDrillDownContent(
              row,
              title,
              drillDownHeader,
              (r) => `${r?.[accessor]}`
            );
          },
        }),
        id: accessor,
        title: title,
        columnConfig: true,
        sort: true,
      };
    }

    return [
      customHeaderColumn,
      createDrillDownColumn(
        'Completed interviews',
        'interviews_completed',
        (row) => `${row?.interviews_completed}`,
        'Completed interviews',
        (info) => {
          const row = info?.rows?.[0]?.original;
          return `${row?.interviews_completed_total}`;
        }
      ),
      createDrillDownColumn(
        'Canceled interviews',
        'interviews_cancelled',
        (row) => `${row?.interviews_cancelled}`,
        'Canceled interviews',
        (info) => {
          const row = info?.rows?.[0]?.original;
          return `${row?.interviews_cancelled_total}`;
        }
      ),
      createDrillDownColumn(
        'Total scorecards',
        'total_scorecards',
        (row) => `${row?.total_scorecards}`,
        'Total scorecards',
        (info) => {
          const row = info?.rows?.[0]?.original;
          return `${row?.total_scorecards_total}`;
        }
      ),
      {
        Header: (
          <HeaderCell
            title="Interview feedback"
            minW="100%"
            textAlign="center"
            textTransform="initial"
          />
        ),
        id: 'interview_feedback',
        title: 'Interview feedback',
        columnConfig: true,
        sort: false,
        columns: [
          createFeedbackColumn('S.Y', 'strong_yes', 'STRONG YES', (info) => {
            const row = info?.rows?.[0]?.original;
            return `${row?.strong_yes_total}`;
          }),
          createFeedbackColumn('S.N', 'strong_no', 'STRONG NO', (info) => {
            const row = info?.rows?.[0]?.original;
            return `${row?.strong_no_total}`;
          }),
          createFeedbackColumn('Y', 'yes', 'YES', (info) => {
            const row = info?.rows?.[0]?.original;
            return `${row?.yes_total}`;
          }),
          createFeedbackColumn('N', 'no', 'NO', (info) => {
            const row = info?.rows?.[0]?.original;
            return `${row?.no_total}`;
          }),
          createFeedbackColumn(
            'Pending',
            'pending_scorecards',
            'Pending scorecards',
            (info) => {
              const row = info?.rows?.[0]?.original;
              return `${row?.pending_scorecards_total}`;
            }
          ),
        ],
      },
      {
        ...createDrillDownColumn(
          'Avg. time spent interviewing',
          'total_interview_duration',
          (row) =>
            secondsToHumanTime(
              parseInt(row?.total_interview_duration) /
                parseInt(row?.interviews_completed)
            ),
          'Avg. time spent interviewing'
        ),
        sortType: (rowA, rowB) => {
          const avgA =
            rowA.original?.interviews_completed &&
            parseInt(rowA.original?.interviews_completed) > 0
              ? parseInt(rowA.values?.total_interview_duration) /
                parseInt(rowA.original?.interviews_completed)
              : 0;
          const avgB =
            rowB.original?.interviews_completed &&
            parseInt(rowB.original?.interviews_completed) > 0
              ? parseInt(rowB.values?.total_interview_duration) /
                parseInt(rowB.original?.interviews_completed)
              : 0;
          return avgA - avgB;
        },
      },
      createDrillDownColumn(
        'Applicants interviewed',
        'applicants_interviewed',
        (row) => `${row?.applicants_interviewed}`,
        'Applicants interviewed',
        (info) => {
          const row = info?.rows?.[0]?.original;
          return `${row?.applicants_interviewed_total}`;
        }
      ),
      {
        ...createDrillDownColumn(
          'Avg. time to fill a scorecard',
          'time_to_fill_scorecard',
          (row) => {
            const total_scorecard =
              parseInt(row?.strong_yes) +
              parseInt(row?.strong_no) +
              parseInt(row?.yes) +
              parseInt(row?.no);
            return secondsToHumanTime(
              parseInt(row?.time_to_fill_scorecard) / total_scorecard
            );
          },
          'Avg. time to fill scorecard'
        ),
        sortType: (rowA, rowB) => {
          const getTotalScorecard = (row) => {
            if (!row) return 0;
            const strongYes = parseInt(row?.strong_yes) || 0;
            const strongNo = parseInt(row?.strong_no) || 0;
            const yes = parseInt(row?.yes) || 0;
            const no = parseInt(row?.no) || 0;
            return strongYes + strongNo + yes + no;
          };
          const totalA = getTotalScorecard(rowA.original);
          const totalB = getTotalScorecard(rowB.original);
          const avgA =
            totalA > 0
              ? parseInt(rowA.values?.time_to_fill_scorecard || 0) / totalA
              : 0;
          const avgB =
            totalB > 0
              ? parseInt(rowB.values?.time_to_fill_scorecard || 0) / totalB
              : 0;
          return avgA - avgB;
        },
      },
    ];
  }, [isReadOnly, groupBy1, groupBy2, feReportsV3, group]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleBreakdownChange = (key: string, option: Option) => {
    if (customBreakdownChange) {
      customBreakdownChange(key, option);
    } else {
      dispatch(applyGroup({ key, value: option }));
    }
  };

  const breakdownConfig = [
    {
      key: 'group_by_1',
      prefix: 'Group by',
      options: GROUP_BY_OPTIONS,
      selected:
        getGroupValue(groupBy1, group?.group_by_1) || GROUP_BY_OPTIONS[0],
    },
    {
      key: 'group_by_2',
      prefix: 'Breakdown by',
      options: BREAKDOWN_OPTIONS,
      selected:
        getGroupValue(groupBy2, group?.group_by_2) || BREAKDOWN_OPTIONS[0],
    },
  ];

  const handleSortChange = (state) => {
    const { sortBy } = state;
    const currentSort = sortBy?.[0];
    onSortChange(currentSort);
  };

  if (isFilterApplied) {
    return (
      <EmptyReportChart
        text="Filters not applicable"
        subText="The applied filters are not applicable for this block"
      />
    );
  }

  return (
    <Wrapper role="group" isReadOnly={isReadOnly || isStaticReport}>
      {consumer === 'DEFAULT' && (
        <Flex
          alignItems="flex-start"
          justifyContent="space-between"
          position="sticky"
          top="0"
          zIndex="6"
          bg="white"
        >
          {renderTitle ? (
            renderTitle()
          ) : (
            <Box flex="1">
              <TitleWithTooltip
                label="Interview breakdown"
                metricDefinition="Comprehensive breakdown of the interview data"
              />
            </Box>
          )}
          <Flex
            gap="8px"
            mr={
              isPrintView
                ? '0'
                : isDataEmpty
                  ? '35px'
                  : isEditMode
                    ? '55px'
                    : '35px'
            }
            mb="8px"
          >
            {breakdownConfig.map(({ key, prefix, options, selected }) => (
              <BreakdownBy
                key={key}
                isPortal={!isFullScreen}
                prefix={prefix}
                options={options}
                selected={selected}
                isEditMode={isEditMode}
                onChange={(option: Option) =>
                  handleBreakdownChange(key, option)
                }
              />
            ))}
          </Flex>
        </Flex>
      )}
      {isHiresTableLoading ? (
        <Center minHeight="200px">
          <Spinner color="primary-800" size="sm" />
        </Center>
      ) : (
        <EmptyDataWrapper data={dataToUse} field="applicants_interviewed">
          <Box className="interview-table-zoom-out" h="100%">
            <Table
              tableId="interview-table"
              skipLocalStorage={skipLocalStorage}
              isColumnLevelSorting
              enableColumnConfig={isEditMode}
              defaultSortBy={[{ id: 'header', desc: false }]}
              columns={columns}
              data={dataToUse}
              enableFooter={true}
              layoutScrollContainerStyles="c-data-table wide-content table-container"
              enableExpanded={true}
              layoutStyles={{
                borderBottom: 'none',
              }}
              getTableState={handleSortChange}
              tableColStyles={tableColStyles}
              onTableColDataChange={onTableColDataChange}
              initialTableColData={initialTableColData}
            />
          </Box>
        </EmptyDataWrapper>
      )}
    </Wrapper>
  );
};
